/**
 * CustomExportModal — « Données personnalisées » : chacun compose son extraction.
 *
 * Contrat POC (JGS, 2026-09-13) : une carte de plus sur l'écran « Données », qui ouvre un
 * modal au GESTE de « Actions groupées » (BulkActionsModal) — même expérience, pas
 * seulement même allure : on choisit le jeu de données dans une grille, puis
 *   A. « Colonnes » : on sélectionne des cartes (recherche, « Tout sélectionner ») ;
 *   B. « Options » : période, U.T., nom du fichier ;
 *   C. « Retenues » : « Ajouter (N) › » y fait passer la sélection, dans l'ordre du
 *      fichier ; on y réordonne, on renvoie une colonne à gauche, on bascule sur l'aperçu.
 * « Télécharger » tient la place de « Terminer ».
 *
 * Ce que l'utilisateur NE peut PAS faire, et c'est voulu : demander une colonne que le
 * jeu ne déclare pas (le serveur répond 400 UNKNOWN_COLUMN), croiser deux jeux, ou sortir
 * de son périmètre d'U.T. (tenu côté serveur, backend/routes/data_export_routes.mjs).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Check, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Download,
  SlidersHorizontal, Loader2, Search, ListTodo, Eye, Columns,
} from 'lucide-react';
import dataExportAPI from '../../api/dataExport.js';

const PREVIEW_DEBOUNCE_MS = 350;
const LABEL_CLASS = 'text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2';
const INPUT_CLASS = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

function CustomExportModal({ t, datasets, sites, singleSite, initialFilters, onClose }) {
  const [datasetKey, setDatasetKey] = useState(null);
  const [selected, setSelected] = useState(new Set());   // colonne A, pas encore ajoutées
  const [retained, setRetained] = useState([]);           // colonne C, ordre du fichier
  const [search, setSearch] = useState('');
  const [range, setRange] = useState({ from: initialFilters.from || '', to: initialFilters.to || '' });
  const [siteId, setSiteId] = useState(initialFilters.siteId || '');
  const [fileName, setFileName] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const dataset = datasets.find((d) => d.key === datasetKey) || null;
  const columns = useMemo(() => dataset?.columns || [], [dataset]);
  const hasPeriod = Boolean(dataset?.filters?.includes('period'));
  const effectiveSiteId = singleSite && sites[0] ? String(sites[0].id) : siteId;
  const labelOf = (key) => columns.find((c) => c.key === key)?.label || key;

  const available = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return columns
      .filter((c) => !retained.includes(c.key))
      .filter((c) => !needle || c.label.toLowerCase().includes(needle));
  }, [columns, retained, search]);

  const params = useMemo(() => ({
    from: hasPeriod ? range.from : undefined,
    to: hasPeriod ? range.to : undefined,
    siteId: effectiveSiteId || undefined,
    columns: retained,
  }), [hasPeriod, range, effectiveSiteId, retained]);

  const chooseDataset = (d) => {
    setDatasetKey(d.key);
    setSelected(new Set());
    setRetained([]);
    setSearch('');
    setFileName('');
    setShowPreview(false);
    setPreview(null);
    setError(null);
  };

  // Aperçu vivant, seulement quand on le regarde : chaque réglage se voit avant de télécharger.
  useEffect(() => {
    if (!showPreview || !datasetKey || retained.length === 0) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setError(null);
      try {
        const res = await dataExportAPI.preview(datasetKey, params);
        if (!cancelled) setPreview(res);
      } catch (err) {
        if (!cancelled) setError(t('dataExport.errors.previewFailed', { message: err.details || err.message }));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showPreview, datasetKey, params, retained.length, t]);

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // « Ajouter (N) › » : la sélection passe à droite, dans l'ordre déclaré du jeu.
  const addSelected = () => {
    const order = columns.map((c) => c.key);
    const incoming = [...selected].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    setRetained((prev) => [...prev, ...incoming.filter((k) => !prev.includes(k))]);
    setSelected(new Set());
  };

  const giveBack = (key) => setRetained((prev) => prev.filter((k) => k !== key));

  const move = (index, delta) => setRetained((prev) => {
    const target = index + delta;
    if (target < 0 || target >= prev.length) return prev;
    const next = [...prev];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      await dataExportAPI.download(datasetKey, params, fileName);
    } catch (err) {
      setError(t('dataExport.errors.downloadFailed', { message: err.message }));
    } finally {
      setDownloading(false);
    }
  };

  const footerHint = !dataset
    ? t('dataExport.custom.hintPickDataset')
    : retained.length > 0
      ? t('dataExport.custom.hintReady', { count: retained.length })
      : t('dataExport.custom.hintSelect');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('dataExport.custom.title')}
        className="bg-white rounded-xl shadow-2xl w-[1040px] max-w-full h-[680px] max-h-[92vh] flex flex-col overflow-hidden"
      >
        {/* ── En-tête ─────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {dataset && (
              <button type="button" onClick={() => setDatasetKey(null)} className="text-white/80 hover:text-white" aria-label={t('dataExport.custom.back')}>
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold">{dataset ? dataset.label : t('dataExport.custom.title')}</h2>
              <p className="text-white/70 text-sm">{dataset ? dataset.description : t('dataExport.custom.pickDataset')}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" aria-label={t('dataExport.custom.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {!dataset ? (
          /* ── Grille de choix du jeu de données ─────────────────── */
          <div className="flex-1 overflow-auto flex items-center justify-center p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-[860px]">
              {datasets.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => chooseDataset(d)}
                  className="bg-blue-50 border-blue-200 border-2 rounded-xl p-5 text-left hover:shadow-md transition-all hover:scale-[1.02]"
                >
                  <SlidersHorizontal className="w-8 h-8 text-blue-500 mb-3" />
                  <h3 className="font-semibold text-blue-700 mb-1">{d.label}</h3>
                  <p className="text-xs text-gray-500">{t('dataExport.custom.columnsAvailable', { count: (d.columns || []).length })}</p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* ── Établi en trois colonnes ─────────────────────────── */
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_240px_1fr] overflow-hidden min-h-0">

            {/* ── A : colonnes disponibles ─────────────────────────── */}
            <div className="flex flex-col border-r border-gray-200 min-h-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  {t('dataExport.custom.columns')}
                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">{available.length}</span>
                  {available.length > 0 && (
                    <span className="ml-auto flex items-center gap-2">
                      {selected.size > 0 && (
                        <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-red-600 hover:text-red-800 font-medium">
                          {t('dataExport.custom.selectNone')}
                        </button>
                      )}
                      <button type="button" onClick={() => setSelected(new Set(available.map((c) => c.key)))} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                        {t('dataExport.custom.selectAll', { count: available.length })}
                      </button>
                    </span>
                  )}
                </h3>
                <div className="mt-2 relative">
                  <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('dataExport.custom.search')}
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {available.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <Check className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm">{t('dataExport.custom.allRetained')}</p>
                  </div>
                ) : (
                  available.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      aria-pressed={selected.has(c.key)}
                      onClick={() => toggle(c.key)}
                      className={`w-full text-left flex items-center justify-between p-3 mb-2 rounded-lg border-2 cursor-pointer transition-all ${
                        selected.has(c.key) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 bg-white'
                      }`}
                    >
                      <span className="font-semibold text-sm text-gray-800">{c.label}</span>
                      <span className="text-xs text-gray-400">{t(`dataExport.custom.types.${c.type || 'text'}`)}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* ── B : options ─────────────────────────────────────── */}
            <div className="flex flex-col bg-gray-50 border-r border-gray-200 min-h-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">{t('dataExport.custom.options')}</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-4">
                <div>
                  <span className={LABEL_CLASS}>{t('dataExport.filters.period')}</span>
                  {hasPeriod ? (
                    <div className="space-y-2">
                      <label className="block text-xs text-gray-500">
                        {t('dataExport.filters.from')}
                        <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className={`mt-1 ${INPUT_CLASS}`} />
                      </label>
                      <label className="block text-xs text-gray-500">
                        {t('dataExport.filters.to')}
                        <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className={`mt-1 ${INPUT_CLASS}`} />
                      </label>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">{t('dataExport.custom.noPeriod')}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="custom-export-site" className={LABEL_CLASS}>{t('dataExport.filters.site')}</label>
                  <select id="custom-export-site" value={effectiveSiteId} onChange={(e) => setSiteId(e.target.value)} className={INPUT_CLASS}>
                    {!singleSite && <option value="">{t('dataExport.filters.allSites')}</option>}
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>{s.site_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="custom-export-filename" className={LABEL_CLASS}>{t('dataExport.custom.fileName')}</label>
                  <input
                    id="custom-export-filename"
                    type="text"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder={t('dataExport.custom.fileNamePlaceholder', { dataset: dataset.key })}
                    className={INPUT_CLASS}
                  />
                </div>
              </div>
            </div>

            {/* ── C : retenues, ou aperçu ─────────────────────────── */}
            <div className="flex flex-col min-h-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  {t('dataExport.custom.retained')}
                  <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded-full">{retained.length}</span>
                  {previewLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                  <button
                    type="button"
                    onClick={() => setShowPreview((v) => !v)}
                    disabled={retained.length === 0}
                    className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-300"
                  >
                    {showPreview ? <Columns className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {showPreview ? t('dataExport.custom.showColumns') : t('dataExport.custom.showPreview')}
                  </button>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {showPreview && preview
                    ? t('dataExport.preview.totalRows', { count: preview.total_rows ?? preview.rows.length })
                    : t('dataExport.custom.retainedHint')}
                </p>
              </div>
              <div className="flex-1 overflow-auto p-3">
                {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
                {retained.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <ListTodo className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm text-center">{t('dataExport.custom.retainedEmpty')}</p>
                  </div>
                ) : showPreview ? (
                  preview && preview.rows.length === 0 ? (
                    <p className="text-sm text-gray-500">{t('dataExport.preview.empty')}</p>
                  ) : preview ? (
                    <table className="min-w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          {preview.columns.map((col) => (
                            <th key={col} className="text-left font-semibold text-gray-700 border-b border-gray-200 px-3 py-2 whitespace-nowrap">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((row, idx) => (
                          <tr key={idx} className="odd:bg-gray-50">
                            {preview.columns.map((col) => (
                              <td key={col} className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">{row[col]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null
                ) : (
                  retained.map((key, i) => (
                    <div key={key} className="flex items-center gap-2 p-2.5 mb-2 rounded-lg bg-green-50 border border-green-200">
                      <span className="w-5 h-5 rounded-full bg-green-500 text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      <span className="flex-1 min-w-0 text-sm font-semibold text-gray-800 truncate">{labelOf(key)}</span>
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30" aria-label={t('dataExport.custom.moveUp', { column: labelOf(key) })}>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === retained.length - 1} className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30" aria-label={t('dataExport.custom.moveDown', { column: labelOf(key) })}>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => giveBack(key)} className="p-1 text-gray-400 hover:text-red-600" aria-label={t('dataExport.custom.remove', { column: labelOf(key) })}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Pied ─────────────────────────────────────────────────── */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-gray-500">{footerHint}</span>
          <div className="flex items-center gap-2">
            {dataset && selected.size > 0 && (
              <span className="text-sm text-blue-700 font-medium mr-2">{t('dataExport.custom.selectedCount', { count: selected.size })}</span>
            )}
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('dataExport.custom.cancel')}
            </button>
            {dataset && (
              <button
                type="button"
                onClick={addSelected}
                disabled={selected.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {t('dataExport.custom.add', { count: selected.size })} <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {dataset && (
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading || retained.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" />
                {downloading ? t('dataExport.downloading') : t('dataExport.custom.download')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomExportModal;
